import React, { Fragment, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { IPopup } from '../../../../utils/types/contentful';
import { Component } from '../../../Contentful/component';
import { modalState } from '../../../../utils/recoil/modal';
import { TeaserPopup } from '../../../Marketing/Popup';
import {
  formatContentfulComponentProps,
  returnLocalisedPath,
} from '../../../../utils/globals';
import { navigate } from 'gatsby';
import { withLocale } from '../../../../utils/hocs';

export const Popup = withLocale(
  ({
    title,
    teaserComponent,
    mainComponent,
    teaserPosition,
    teaserScrollTrigger,
    teaserTimerTrigger,
    mainPosition,
    mainScrollTrigger,
    mainTimerTrigger,
    trigger,
    transition,
    locale,
  }: IPopup) => {
    const { link } = formatContentfulComponentProps(teaserComponent);
    const [modal, setModal] = useRecoilState(modalState);
    const [hasOpened, setHasOpened] = useState(false);
    const [isScrollBeforeTimeOut, setIsScrollBeforeTimeOut] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);

    const handleMainPopupOpen = () => {
      setHasOpened(true);
      setModal({
        ...modal,
        show: true,
        hasOverlay: true,
        placement: mainPosition,
        children: <Component {...mainComponent} />,
      });
    };

    const handleTeaserPopupOpen = () => {
      setHasOpened(true);
      setModal({
        ...modal,
        show: true,
        placement: teaserPosition,
        hasOverlay: false,
        children: (
          <button
            type='button'
            className='cursor-pointer'
            onClick={
              link?.slug?.url
                ? () => {
                    setModal({ ...modal, show: false });
                    navigate(returnLocalisedPath(locale[0], link.slug.url));
                  }
                : handleMainPopupOpen
            }
          >
            <TeaserPopup {...formatContentfulComponentProps(teaserComponent)} />
          </button>
        ),
      });
    };

    const handleOpenPopup = (open) => {
      const popupKey = title.replace(' ', '_');
      // handle triggers parameters
      if (trigger === 'pageLoad' && !hasOpened) {
        open();
      } else if (trigger === 'newSession') {
        if (
          typeof window !== 'undefined' &&
          !window.sessionStorage[`has_seen_popup_${popupKey}`]
        ) {
          open();
          window.sessionStorage[`has_seen_popup_${popupKey}`] = true;
        }
      } else if (trigger === 'newUser') {
        if (
          typeof window !== 'undefined' &&
          !window.localStorage[`has_seen_popup_${popupKey}`]
        ) {
          open();
          window.localStorage[`has_seen_popup_${popupKey}`] = true;
        }
      }
    };

    // Set scroll event listner on mount.
    useEffect(() => {
      if (typeof window !== 'undefined' && mainScrollTrigger) {
        const onScroll = (e) => {
          if (e.target.documentElement) {
            setScrollTop(e.target.documentElement.scrollTop);
          }
        };
        window.addEventListener('scroll', onScroll);

        return () => window.removeEventListener('scroll', onScroll);
      }

      if (typeof window !== 'undefined' && teaserScrollTrigger) {
        const onScroll = (e) => {
          if (e.target.documentElement) {
            setScrollTop(e.target.documentElement.scrollTop);
          }
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
      }
    }, []);

    // Set Timeout on mount & clear Timeout on scroll close
    useEffect(() => {
      if (mainTimerTrigger && !teaserComponent) {
        const mainDialogTimeOut = setTimeout(() => {
          handleOpenPopup(handleMainPopupOpen);
        }, mainTimerTrigger * 1000);
        return () => {
          clearTimeout(mainDialogTimeOut);
        };
      }

      if (teaserTimerTrigger) {
        const teaserTimeOut = setTimeout(() => {
          handleOpenPopup(handleTeaserPopupOpen);
        }, teaserTimerTrigger * 1000);
        return () => {
          clearTimeout(teaserTimeOut);
        };
      }
    }, [isScrollBeforeTimeOut]);

    useEffect(() => {
      // If main popup has a scroll trigger AND current scroll passes beyond scroll trigger.
      if (
        !teaserComponent &&
        scrollTop > mainScrollTrigger &&
        mainScrollTrigger > 0 &&
        mainComponent
      ) {
        handleOpenPopup(handleMainPopupOpen);
        setIsScrollBeforeTimeOut(true);
      }

      // If main popup has a scroll trigger AND current scroll passes beyond scroll trigger.
      if (scrollTop > teaserScrollTrigger && teaserScrollTrigger > 0) {
        handleOpenPopup(handleTeaserPopupOpen);
        setIsScrollBeforeTimeOut(true);
      }
    }, [scrollTop]);

    return <></>;
  }
);
